import type { ViewStyle } from 'react-native'

import { Box, Text, useTheme } from '../theme/restyle'
import { FALLBACK_ASSET_LOGO } from './assetLogos'
import { FigmaPlusIcon } from './FigmaSystemIcons'
import { FunIcon } from './FunIcon'
import { chainIconUri } from './funIconUri'

/**
 * The crypto-row trailing avatar stack (Figma: Add money `list-item/base` →
 * `trailing` → `crypto2`). A short row of supported source-asset logos —
 * 16px thin-bordered circles — followed by a "+N" pill counting the rest, e.g.
 * `◇ ◎ ⬡ ⊖ +7`. Logos resolve from CDN chain keys so the package stays
 * asset-free.
 */
export interface SourceTokenClusterProps {
  /**
   * Logos to show — either CDN chain **keys** (e.g. `['ethereum', 'base', …]`)
   * or full image **URLs** (e.g. `@funkit/chains` `iconUrl`s); each entry is
   * resolved independently. The first `maxVisible` render as logos; the rest
   * collapse into the "+N" pill.
   */
  icons: string[]
  /** Logos shown before collapsing the rest into "+N". Defaults to 4 (Figma). */
  maxVisible?: number
  /** Logo diameter in px. Defaults to 16 (Add money row); 20 on Transfer. */
  size?: number
  /** Gap between chips. Figma: 2 on the Add money row, 4 on Transfer. */
  gap?: number
  /**
   * Overflow chip style. `'count'` (default) shows "+N" with the remaining count
   * in a `pillPressed` circle (Transfer deposit row, Figma `tap-target`).
   * `'plus'` shows a bare "+" glyph in a `bgSecondary` circle regardless of count
   * (Add money Crypto row, Figma `crypto2`).
   */
  overflowMode?: 'count' | 'plus'
}

/** A full URL is used as-is; anything else is treated as a CDN chain key. */
function toIconUri(icon: string): string {
  return icon.startsWith('http') ? icon : chainIconUri(icon)
}

export function SourceTokenCluster({
  icons,
  maxVisible = 4,
  size = 16,
  gap = 2,
  overflowMode = 'count',
}: SourceTokenClusterProps) {
  const theme = useTheme()
  const visible = icons.slice(0, maxVisible)
  const overflow = icons.length - visible.length
  return (
    <Box flexDirection="row" alignItems="center" style={{ gap }}>
      {visible.map((icon) => (
        <Box
          key={icon}
          style={[
            iconRing(size),
            { backgroundColor: theme.background.secondary },
          ]}
        >
          <FunIcon
            uri={toIconUri(icon)}
            size={size}
            fallbackUri={FALLBACK_ASSET_LOGO}
          />
        </Box>
      ))}
      {overflow > 0 ? (
        // Both chips are a fixed `size`×`size` circle (Figma). The "+" glyph sits
        // on `bgSecondary` (Add money `crypto2`); the "+N" count on `pillPressed`
        // (Transfer `tap-target`).
        <Box
          alignItems="center"
          justifyContent="center"
          style={[
            overflowCircle(size),
            {
              backgroundColor:
                overflowMode === 'plus'
                  ? theme.background.secondary
                  : theme.background.pill.pressed,
            },
          ]}
        >
          {overflowMode === 'plus' ? (
            // The geometric "+" glyph centers cleanly; the text "+" did not.
            <FigmaPlusIcon
              size={size}
              color={theme.content.elements.secondary}
            />
          ) : (
            <Text
              variant="bodyMedium"
              style={[
                overflowText(size),
                { color: theme.content.elements.secondary },
              ]}
            >
              {`+${overflow}`}
            </Text>
          )}
        </Box>
      ) : null}
    </Box>
  )
}

// A logo in a circle with the Figma `border/icon` hairline. `overflow: hidden`
// clips the CDN logo to the circle so the border reads cleanly.
const iconRing = (size: number): ViewStyle => ({
  width: size,
  height: size,
  borderRadius: 999,
  borderWidth: 1,
  borderColor: 'rgba(0,0,0,0.1)',
  overflow: 'hidden',
  // Center the same-size FunIcon so the border-box hairline doesn't push it 1px
  // off and clip the bottom-right; it fills the circle with the border on edge.
  alignItems: 'center',
  justifyContent: 'center',
})
// Both overflow chips are a fixed `size`×`size` circle (Figma) — no horizontal
// padding (which made the single-glyph "+" read as an oval).
const overflowCircle = (size: number): ViewStyle => ({
  width: size,
  height: size,
  borderRadius: 999,
})
// "+N" count text: Inter-Medium (via `bodyMedium`) at ~0.6× the chip, tracking
// -0.48 per the Figma `tap-target` (`+4`).
const overflowText = (size: number) =>
  ({
    fontSize: Math.round(size * 0.6),
    letterSpacing: -0.48,
  }) as const
